/**
 * Player Sprite Sheet Renderer
 * Sheet: 9 columns × 4 rows of 64×64 frames
 * Row 0 = up, Row 1 = left, Row 2 = down, Row 3 = right
 *
 * Supports two characters (male/female) and a hue rotation tint per render.
 */

const SPRITE_V = '?v=5';
export const SPRITE_PATHS = {
  male: `/sprites/player_sheet.png${SPRITE_V}`,
  mage: `/sprites/player_mage.png${SPRITE_V}`,
  athlete: `/sprites/player_athlete.png${SPRITE_V}`,
  punk: `/sprites/player_punk.png${SPRITE_V}`,
  royalty: `/sprites/player_royalty.png${SPRITE_V}`,
  formal: `/sprites/player_formal.png${SPRITE_V}`,
  dj: `/sprites/player_dj.png${SPRITE_V}`,
  vampire: `/sprites/player_vampire.png${SPRITE_V}`,
  ninja: `/sprites/player_ninja.png${SPRITE_V}`,
  pilot: `/sprites/player_pilot.png${SPRITE_V}`,
  cowboy: `/sprites/player_cowboy.png${SPRITE_V}`,
  surfer: `/sprites/player_surfer.png${SPRITE_V}`,
  pirate: `/sprites/player_pirate.png${SPRITE_V}`,
  astronaut: `/sprites/player_astronaut.png${SPRITE_V}`,
  knight: `/sprites/player_knight.png${SPRITE_V}`,
  chef: `/sprites/player_chef.png${SPRITE_V}`,
  zombie: `/sprites/player_zombie.png${SPRITE_V}`,
  alien: `/sprites/player_alien.png${SPRITE_V}`,
  samurai: `/sprites/player_samurai.png${SPRITE_V}`,
  clown: `/sprites/player_clown.png${SPRITE_V}`,
  devil: `/sprites/player_devil.png${SPRITE_V}`,
  angel: `/sprites/player_angel.png${SPRITE_V}`,
  skeleton: `/sprites/player_skeleton.png${SPRITE_V}`,
  werewolf: `/sprites/player_werewolf.png${SPRITE_V}`,
  boxer: `/sprites/player_boxer.png${SPRITE_V}`,
  firefighter: `/sprites/player_firefighter.png${SPRITE_V}`,
  doctor: `/sprites/player_doctor.png${SPRITE_V}`,
  wizard: `/sprites/player_wizard.png${SPRITE_V}`,
  ballerina: `/sprites/player_ballerina.png${SPRITE_V}`,
  base_m: `/sprites/player_base_m.png${SPRITE_V}`,
  base_f: `/sprites/player_base_f.png${SPRITE_V}`,
};

export const VALID_CHARACTERS = Object.keys(SPRITE_PATHS);

// Skin tone → target mid-tone color (real palette-swap of skin pixels)
export const SKIN_HUE = {
  pale: 0, light: 0, fair: 0, rosy: -10,
  medium: 0, olive: 5, tan: 10, sun_kissed: 12,
  brown: 18, mocha: 22, dark: 25, deep: 28, ebony: 30,
  undead: 90, cyber_white: 220, demon_red: -20,
};

// Target skin colors — pixels classified as skin get remapped to this ramp
export const SKIN_COLORS = {
  pale: [255, 228, 208],
  light: [252, 216, 186],
  fair: [245, 205, 170],
  rosy: [246, 196, 172],
  medium: null, // sheet default — no recolor
  olive: [206, 168, 116],
  tan: [186, 140, 96],
  sun_kissed: [196, 146, 88],
  brown: [150, 100, 56],
  mocha: [122, 82, 48],
  dark: [100, 68, 42],
  deep: [76, 50, 30],
  ebony: [56, 36, 22],
  undead: [158, 178, 140],
  cyber_white: [226, 220, 240],
  demon_red: [188, 88, 78],
};

export const computeTint = (avatar) => {
  const sprite = typeof avatar?.spriteHue === 'number' ? avatar.spriteHue : 0;
  const sat = typeof avatar?.spriteSat === 'number' ? avatar.spriteSat : 100;
  const bright = typeof avatar?.spriteBright === 'number' ? avatar.spriteBright : 100;
  const skin = SKIN_COLORS[avatar?.skinTone] ? avatar.skinTone : null;
  return { hue: sprite, sat, bright, skin };
};

// Accept legacy number tints (hue only) or the full tint object
const normalizeTint = (t) => {
  if (typeof t === 'number') return { hue: t, sat: 100, bright: 100, skin: null };
  if (!t) return { hue: 0, sat: 100, bright: 100, skin: null };
  return {
    hue: t.hue || 0,
    sat: typeof t.sat === 'number' ? t.sat : 100,
    bright: typeof t.bright === 'number' ? t.bright : 100,
    skin: t.skin || null,
  };
};

export const resolveGender = (raw) => (SPRITE_PATHS[raw] ? raw : 'male');
const FRAME_W = 64;
const FRAME_H = 64;
const COLS = 9;

const ROW = { up: 0, left: 1, down: 2, right: 3 };

// Image cache per gender
const sheetImages = {};   // gender -> Image
const sheetLoaded = {};   // gender -> bool

// Pre-tinted (gender, tint) -> offscreen canvas
const tintedSheetCache = new Map();
const cacheKey = (gender, t) => `${gender}:${t.hue}:${t.sat}:${t.bright}:${t.skin || ''}`;

// Classify a pixel as skin: peachy hue band, moderate saturation, decently bright
const isSkinPixel = (r, g, b) => {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const v = max / 255;
  if (v < 0.55 || max === 0) return false;
  const s = (max - min) / max;
  if (s < 0.16 || s > 0.62) return false;
  const d = max - min;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = ((h * 60) + 360) % 360;
  return h >= 8 && h <= 40;
};

const applySkinRecolor = (canvas, skin) => {
  const target = SKIN_COLORS[skin];
  if (!target) return;
  const ctx = canvas.getContext('2d');
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const BASE_LUM = 224; // avg luminance of the sheets' default skin
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] < 20) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    if (!isSkinPixel(r, g, b)) continue;
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / BASE_LUM;
    d[i] = Math.min(255, Math.round(target[0] * lum));
    d[i + 1] = Math.min(255, Math.round(target[1] * lum));
    d[i + 2] = Math.min(255, Math.round(target[2] * lum));
  }
  ctx.putImageData(img, 0, 0);
};

const buildTintedSheet = (gender, t) => {
  const img = sheetImages[gender];
  if (!sheetLoaded[gender] || !img) return null;
  const off = document.createElement('canvas');
  off.width = img.width;
  off.height = img.height;
  const octx = off.getContext('2d');
  octx.drawImage(img, 0, 0);
  if (t.skin) applySkinRecolor(off, t.skin);
  if (t.hue !== 0 || t.sat !== 100 || t.bright !== 100) {
    const off2 = document.createElement('canvas');
    off2.width = off.width;
    off2.height = off.height;
    const o2 = off2.getContext('2d');
    o2.filter = `hue-rotate(${t.hue}deg) saturate(${t.sat}%) brightness(${t.bright}%)`;
    o2.drawImage(off, 0, 0);
    o2.filter = 'none';
    return off2;
  }
  return off;
};

const getSheet = (gender, tint) => {
  if (!sheetLoaded[gender]) return null;
  const t = normalizeTint(tint);
  t.hue = ((Math.round(t.hue) % 360) + 360) % 360;
  t.sat = Math.round(t.sat);
  t.bright = Math.round(t.bright);
  if (t.hue === 0 && t.sat === 100 && t.bright === 100 && !t.skin) return sheetImages[gender];
  const key = cacheKey(gender, t);
  if (tintedSheetCache.has(key)) return tintedSheetCache.get(key);
  const tinted = buildTintedSheet(gender, t);
  if (tinted) {
    if (tintedSheetCache.size > 32) {
      const firstKey = tintedSheetCache.keys().next().value;
      tintedSheetCache.delete(firstKey);
    }
    tintedSheetCache.set(key, tinted);
  }
  return tinted;
};

export const ensureSpriteLoaded = (gender = 'male') => {
  const g = SPRITE_PATHS[gender] ? gender : 'male';
  if (sheetImages[g]) return sheetImages[g];
  const img = new Image();
  img.onload = () => { sheetLoaded[g] = true; };
  img.onerror = () => { console.error(`Failed to load player sprite sheet: ${g}`); };
  img.src = SPRITE_PATHS[g];
  sheetImages[g] = img;
  return img;
};

export const isSpriteReady = (gender = 'male') => !!sheetLoaded[gender];

// Preload both sheets
export const preloadAllSprites = () => {
  Object.keys(SPRITE_PATHS).forEach(g => ensureSpriteLoaded(g));
};

/**
 * Draw the player sprite at (x, y).
 * @param {string} gender - 'male' | 'female'
 * @param {number} hue - 0..359 hue rotation in degrees
 */
export const drawPlayerSprite = (
  ctx,
  x,
  y,
  direction = 'down',
  isWalking = false,
  walkPhase = 0,
  scale = 1.4,
  hue = 0,
  gender = 'male'
) => {
  const g = SPRITE_PATHS[gender] ? gender : 'male';
  ensureSpriteLoaded(g);
  const dW = FRAME_W * scale;
  const dH = FRAME_H * scale;
  const drawX = x - dW / 2;
  const drawY = y - dH + 6 * scale;

  if (!sheetLoaded[g]) {
    ctx.fillStyle = 'rgba(60,60,80,0.6)';
    ctx.fillRect(drawX, drawY, dW, dH);
    return;
  }

  const sheet = getSheet(g, hue) || sheetImages[g];
  const row = ROW[direction] !== undefined ? ROW[direction] : ROW.down;
  const col = isWalking
    ? 1 + (Math.floor(walkPhase) % (COLS - 1))
    : 0;

  const sx = col * FRAME_W;
  const sy = row * FRAME_H;

  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(sheet, sx, sy, FRAME_W, FRAME_H, drawX, drawY, dW, dH);
};

/**
 * Compute direction from a movement delta.
 */
export const directionFromDelta = (dx, dy, threshold = 0.5) => {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  if (adx < threshold && ady < threshold) return null;
  if (adx > ady) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'down' : 'up';
};
